import { useState, useRef, useEffect } from 'react'
import { Modal, notification, Spin } from 'antd'
import { AiAgentMessage, AiAgentProps, aiAgentRoles } from './ai-agent.type'
import { sendMessageToAgent, requestComparisonPdfFromCsv } from './ai-agent.service'
import { subscribeToChatRequests, setChatBusy } from './libs/ai-agent-bus'
import {
  acquireChatSlot,
  releaseChatSlot,
  startChatHeartbeat,
  getMaxConcurrentChats,
} from './libs/chat-concurrency'
import { AiAgentFab } from './components/AiAgentFab'
import { AiAgentChatBox } from './components/AiAgentChatBox'
import { AiAgentConsentModal } from './components/AiAgentConsentModal'
import { AiAgentIntroModal } from './components/AiAgentIntroModal'
import { AiAgentEmailModal } from './components/AiAgentEmailModal'
import { Filter } from 'bad-words'
import { useChatContext } from '../hooks'
import { toCsvContentFromTables } from './libs/csv-export'

import './styles/ai-agent.chat.less'
import './styles/ai-agent.keydiffs.less'
import './styles/ai-agent.layout.less'
import './styles/ai-agent.input.less'
import './styles/ai-agent.popup.less'
import './styles/ai-agent.avatar.less'
import './styles/ai-agent.animations.less'
import './styles/ai-agent.fab.less'
import './styles/ai-agent.ellipsis.less'
import './styles/ai-agent.mixins.less'
import './styles/ai-agent.variables.less'
import './styles/ai-agent.common-questions.less'
import './styles/ai-agent.bubbles.less'
import './styles/ai-agent.markdown.less'
import './styles/ai-agent.consent.less'

const MAX_MESSAGE_LENGTH = 300
const filter = new Filter()

// Persisted state keys
const CONSENT_KEY = 'pci_ai_agent_consent_v1'
const INTRO_KEY = 'pci_ai_agent_intro_seen_v1'
export const HAS_HISTORY_KEY = 'pci_ai_agent_has_history_v1'

function AiAgent({ initialMessages, showTimestamp = true }: AiAgentProps) {
  // refs for chat box elements
  const chatEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const resetKeyRef = useRef<string | undefined>(undefined)

  const {
    prepareHistoryWithContext,
    getCoverIds,
    getSelectedPolicyContents,
    getAllPolicies,
    getUserId,
    getPolicyIds,
    getCountryCode,
    contextToggles,
    getInitialMessages,
    hasCompleteContext,
    hasClassAndCoverSelections,
    getInsuranceClass,
    getPolicyCodes,
    isAdmin,
    openSelectionModal,
  } = useChatContext()

  const selectedPolicyContents = getSelectedPolicyContents()
  const allPolicies = getAllPolicies()

  const fabSteps = [
    {
      step: 1,
      completed: hasClassAndCoverSelections,
      label: 'Select a product class & covers',
    },
    {
      step: 2,
      completed: selectedPolicyContents.length >= 2,
      label: 'Select 2 policies to compare',
      onClick: openSelectionModal,
    },
    {
      step: 3,
      completed: false,
      label: 'Generate key differences report',
    },
  ]

  // Chat interface state
  const [messages, setMessages] = useState<AiAgentMessage[]>(initialMessages || [])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [regeneratingMessageIndex, setRegeneratingMessageIndex] = useState<number | null>(null)
  const [isClosing, setIsClosing] = useState(false)
  const [inputError, setInputError] = useState<string | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  // Consent gate for AI usage
  const [hasConsented, setHasConsented] = useState(false)
  const [isConsentVisible, setIsConsentVisible] = useState(false)
  const [isConsentChecked, setIsConsentChecked] = useState(false)

  // Intro gate for AL introduction
  const [isIntroVisible, setIsIntroVisible] = useState(false)
  const [releaseHeartbeat, setReleaseHeartbeat] = useState<null | (() => void)>(null)
  const [isEmailOpen, setIsEmailOpen] = useState(false)
  const [emailHtml, setEmailHtml] = useState<string | undefined>(undefined)
  const [emailSubject, setEmailSubject] = useState<string | undefined>(undefined)

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded)
  }
  function handleOpenEmail({ html, subject }: { html?: string; subject?: string }) {
    setEmailHtml(html || '')
    setEmailSubject(subject || 'AL Response')
    setIsEmailOpen(true)
  }

  function handleOpenRequested() {
    if (!hasConsented) {
      setIsConsentChecked(false)
      setIsConsentVisible(true)
      return
    }
    const { allowed } = acquireChatSlot()
    if (!allowed) {
      Modal.info({
        icon: null,
        content: `You have reached the maximum number of open chats (${getMaxConcurrentChats()}). 
                  Please close another chat or try again later.`,
        okText: 'OK',
      })
      return
    }
    const stop = startChatHeartbeat()
    setReleaseHeartbeat(() => stop)
    setIsOpen(true)
  }

  /**
   * A question asked from the comparison table. It cannot be sent on arrival — the chat
   * may still be shut, or waiting on consent, or without its context, or part way through
   * another answer — so it is held until the effect below finds all four settled.
   *
   * State, not a ref: with the chat already open none of the other conditions change when
   * a request lands, so a ref left nothing to re-run the effect and the question sat there
   * unsent until something else happened to re-render.
   */
  const [pendingRequest, setPendingRequest] = useState<string | null>(null)

  // The buttons that ask AL things go inactive while he is answering, so they cannot stack
  // up duplicates of the same question.
  useEffect(() => {
    setChatBusy(isLoading)
    return () => setChatBusy(false)
  }, [isLoading])

  useEffect(
    () =>
      subscribeToChatRequests((message) => {
        // Belt and braces with the disabled button: a click can still land in the gap
        // before isLoading has rendered, and queueing it would answer the same question
        // twice over.
        if (isLoading) return
        setPendingRequest(message)
        if (!isOpen) handleOpenRequested()
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen, hasConsented, isLoading],
  )

  useEffect(() => {
    if (!pendingRequest || !isOpen) return
    if (!hasCompleteContext || isLoading) return

    setPendingRequest(null)
    sendMessage(pendingRequest)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRequest, isOpen, hasCompleteContext, isLoading])

  function handleInputChange(value: string) {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current)
    }
    if (value.length <= MAX_MESSAGE_LENGTH && !filter.isProfane(value)) {
      setInputError(null)
      setInput(value)
      return
    }
    setInput(value)
    errorTimeoutRef.current = setTimeout(() => {
      if (value.length > MAX_MESSAGE_LENGTH) {
        setInputError(`Message too long (max ${MAX_MESSAGE_LENGTH} characters).`)
      } else if (filter.isProfane(value)) {
        setInputError('Please avoid using inappropriate language.')
      }
    }, 650)
  }

  function handleSend() {
    if (
      !hasCompleteContext ||
      isLoading ||
      !input.trim() ||
      input.length > MAX_MESSAGE_LENGTH ||
      filter.isProfane(input)
    )
      return

    sendMessage(input)
  }

  function handleQuestionSelect(question: string) {
    if (!hasCompleteContext || isLoading) return
    sendMessage(question)
  }

  async function handleRegenerate(messageId: string) {
    if (!hasCompleteContext || isLoading) return

    // Find the message to regenerate
    const messageIndex = messages.findIndex((msg) => msg.id === messageId)
    if (messageIndex === -1 || messageIndex === 0) return // Can't regenerate first message or if not found

    // Find the user message that preceded this assistant message
    const userMessageIndex = messageIndex - 1
    const userMessage = messages[userMessageIndex]

    if (userMessage.sender !== aiAgentRoles.user) return

    // Remove messages after the target message first
    setMessages((prev) => prev.slice(0, messageIndex))

    // Add a prompt to indicate regeneration
    const regeneratedPrompt = `${userMessage.text}\n\nPlease provide a different perspective or alternative approach`

    // Then send the regeneration request with the modified prompt
    sendMessage(regeneratedPrompt, true, messageIndex)
  }

  async function sendMessage(messageText: string, isRegenerate?: boolean, messageIndex?: number) {
    const userMsg: AiAgentMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text: messageText,
      timestamp: Date.now(),
    }

    if (isRegenerate) {
      setRegeneratingMessageIndex(messageIndex ?? null)
    }

    if (!isRegenerate) {
      setMessages((prev) => [...prev, userMsg])
    }

    setInput('')
    setIsLoading(true)

    const historyToSend = prepareHistoryWithContext(messages)
    const coverIds = getCoverIds()
    const insuranceClass = getInsuranceClass()
    const policyCodes = getPolicyCodes()

    const userId = getUserId()
    const uniquePolicyIds = getPolicyIds()
    const countryCode = getCountryCode()

    sendMessageToAgent({
      message: messageText,
      history: historyToSend,
      coverIds,
      insuranceClass,
      policyCodes,
      selectedPolicyContents,
      allPolicies,
      userId,
      policyIds: uniquePolicyIds,
      countryCode,
    })
      .then((botMsg) => {
        if (isRegenerate) {
          // Replace the target message with the new response
          setMessages((prev) => [
            ...prev.slice(0, messageIndex),
            { ...botMsg, timestamp: Date.now() },
          ])
          setRegeneratingMessageIndex(null)
        } else {
          setMessages((prev) => [...prev, { ...botMsg, timestamp: Date.now() }])
        }
      })
      .finally(() => setIsLoading(false))
  }

  function handleClose() {
    setIsClosing(true)
    if (isExpanded) {
      handleToggleExpand()
    }
  }

  function handleAnimationEnd() {
    if (isClosing) {
      setIsOpen(false)
      setIsClosing(false)
      if (releaseHeartbeat) releaseHeartbeat()
      releaseChatSlot()
    }
  }

  function handleReset() {
    Modal.confirm({
      icon: null,
      content:
        'Are you sure you want to start a new chat? This will clear your current conversation.',
      okText: 'Yes, start new',
      cancelText: 'Cancel',
      onOk: () => {
        setMessages([])
        setInput('')
      },
    })
  }

  async function handleDownloadPdf({
    html,
    format = 'Pdf',
  }: {
    html: string
    frontMatter?: string
    format?: 'Pdf' | 'Docx'
  }) {
    try {
      const container = document.createElement('div')
      container.innerHTML = html || ''
      const tables = Array.from(container.querySelectorAll('table')) as HTMLTableElement[]

      if (!tables.length) {
        notification.error({
          key: 'pdf_no_tables',
          message: 'No comparison tables found to generate the report',
        })
        return
      }

      const { csvContent, timestamp } = toCsvContentFromTables(tables)
      notification.open({
        key: 'download_generating',
        message: (
          <>
            <Spin size='small' /> <span style={{ marginLeft: 12 }}>Generating report...</span>
          </>
        ),
      })

      const blob = await requestComparisonPdfFromCsv({
        csvContent,
        filename: `al-comparison-${timestamp}.csv`,
        format,
      })

      notification.close('download_generating')
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const extension = format === 'Docx' ? 'docx' : 'pdf'
      a.download = `al-comparison-${timestamp}.${extension}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 0)
      notification.success({ key: 'download_success', message: 'Report downloaded successfully' })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err)
      notification.close('download_generating')
      notification.error({ key: 'download_error', message: 'Failed to download report' })
    }
  }

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isOpen])

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus()
  }, [isOpen])

  useEffect(() => {
    // Read persisted consent on mount
    try {
      const saved = localStorage.getItem(CONSENT_KEY)
      if (saved === 'true') setHasConsented(true)
      // initialize history flag if missing
      const hasHistory = localStorage.getItem(HAS_HISTORY_KEY)
      if (hasHistory === null) localStorage.setItem(HAS_HISTORY_KEY, 'false')
    } catch {
      // ignore storage access errors
    }
  }, [])

  useEffect(() => {
    // Show intro only when the chat interface is opened, and only the first time
    if (!isOpen) return
    try {
      const introSeen = localStorage.getItem(INTRO_KEY)
      if (introSeen !== 'true') setIsIntroVisible(true)
    } catch {
      // ignore storage access errors
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current)
      }
      if (releaseHeartbeat) releaseHeartbeat()
      releaseChatSlot()
    }
  }, [releaseHeartbeat])

  // Persist whether there is chat history (more than the first assistant message)
  useEffect(() => {
    try {
      const hasHistory = messages.length > 1
      localStorage.setItem(HAS_HISTORY_KEY, hasHistory ? 'true' : 'false')
    } catch {
      // ignore storage access errors
    }
  }, [messages])

  // Reset chat when the comparison context (class or covers) changes
  useEffect(() => {
    const insuranceClass = getInsuranceClass()
    const nextKey = `${insuranceClass || ''}`

    if (resetKeyRef.current === undefined) {
      resetKeyRef.current = nextKey
      return
    }

    if (resetKeyRef.current !== nextKey) {
      setMessages([])
      setInput('')
      try {
        localStorage.setItem(HAS_HISTORY_KEY, 'false')
      } catch {
        // ignore storage access errors
      }
      resetKeyRef.current = nextKey
    }
  }, [getInsuranceClass])

  if (!isOpen) {
    return (
      <>
        <AiAgentFab
          message={
            messages.length > 1 ? 'Click to continue chatting' : 'Hello! I am AL Your AI assistant'
          }
          onOpen={handleOpenRequested}
          hasMessages={messages.length > 1 && !isOpen}
          isAvailable={hasCompleteContext}
          steps={fabSteps}
        />

        <AiAgentConsentModal
          open={isConsentVisible}
          checked={isConsentChecked}
          onCheckedChange={setIsConsentChecked}
          onOk={() => {
            try {
              localStorage.setItem(CONSENT_KEY, 'true')
            } catch {
              // ignore storage access errors
            }
            setHasConsented(true)
            setIsConsentVisible(false)
            setIsOpen(true)
          }}
          onCancel={() => setIsConsentVisible(false)}
        />
      </>
    )
  }

  return (
    <>
      <AiAgentChatBox
        initialMessages={getInitialMessages()}
        messages={messages}
        input={input}
        isLoading={isLoading}
        isClosing={isClosing}
        chatEndRef={chatEndRef}
        inputRef={inputRef}
        onInputChange={handleInputChange}
        onInputKeyDown={(e) => {
          if (e.key === 'Enter') handleSend()
        }}
        onSend={handleSend}
        onReset={handleReset}
        onClose={handleClose}
        onAnimationEnd={handleAnimationEnd}
        showTimestamp={showTimestamp}
        maxLength={MAX_MESSAGE_LENGTH}
        inputError={inputError}
        onQuestionSelect={handleQuestionSelect}
        contextToggles={contextToggles}
        isExpanded={isExpanded}
        onToggleExpand={handleToggleExpand}
        isAvailable={hasCompleteContext}
        onRegenerate={handleRegenerate}
        regeneratingMessageIndex={regeneratingMessageIndex}
        onOpenIntro={() => setIsIntroVisible(true)}
        selectedPolicyContents={selectedPolicyContents}
        selectedPolicyCodes={getPolicyCodes()}
        isAdmin={isAdmin}
        onDownloadPdf={handleDownloadPdf}
        onOpenEmail={handleOpenEmail}
        onOpenPolicySelection={openSelectionModal}
      />

      <AiAgentConsentModal
        open={isConsentVisible}
        checked={isConsentChecked}
        onCheckedChange={setIsConsentChecked}
        onOk={() => {
          try {
            localStorage.setItem(CONSENT_KEY, 'true')
          } catch {
            // ignore storage access errors
          }
          setHasConsented(true)
          setIsConsentVisible(false)
          setIsOpen(true)
        }}
        onCancel={() => setIsConsentVisible(false)}
      />

      <AiAgentIntroModal
        open={isIntroVisible}
        onClose={() => {
          try {
            localStorage.setItem(INTRO_KEY, 'true')
          } catch {
            // ignore storage access errors
          }
          setIsIntroVisible(false)
        }}
      />

      <AiAgentEmailModal
        open={isEmailOpen}
        onClose={() => setIsEmailOpen(false)}
        html={emailHtml}
        subject={emailSubject}
      />
    </>
  )
}

export { AiAgent }
